<template>
  <Layout>
    <PageHeader :title="'My Orders'" :items="breadcrumbs" />
    <div class="member-orders" :style="{ paddingBottom: '64px' }">

      <!-- Loading state -->
      <div v-if="ordersLoading" class="py-4">
        <b-card v-for="n in 3" :key="'skeleton-' + n" class="skeleton-card mb-3" no-body>
          <div class="skeleton-content p-3">
            <div class="skeleton-line w-50 mb-2"></div>
            <div class="skeleton-line w-75 mb-2"></div>
            <div class="skeleton-line w-25"></div>
          </div>
        </b-card>
      </div>

      <!-- Empty state -->
      <div v-else-if="!hasOrders" class="text-center py-5">
        <i class="bx bx-receipt" style="font-size: 48px; color: var(--text-muted)"></i>
        <p class="text-muted mt-3">No orders yet</p>
        <b-button variant="primary" @click="handleNavigate('shop')">Browse Products</b-button>
      </div>

      <!-- Order list -->
      <div v-else>
        <b-card
          v-for="order in memberOrders"
          :key="order.id"
          class="order-card mb-3"
          no-body
          @click="toggleOrder(order.id)"
          role="button"
        >
          <div class="order-header d-flex justify-content-between align-items-center p-3">
            <div>
              <span class="order-number font-monospace">{{ order.order_no }}</span>
              <span class="order-date text-muted ml-2">{{ order.created_date }}</span>
            </div>
            <div class="d-flex align-items-center">
              <b-badge :variant="statusVariant(order.status_name)" class="mr-2">
                {{ order.status_name }}
              </b-badge>
              <strong class="order-total">R{{ formatPrice(order.order_total) }}</strong>
              <i class="bx ml-2" :class="expandedOrderId === order.id ? 'bx-chevron-up' : 'bx-chevron-down'"></i>
            </div>
          </div>
          <div class="order-summary px-3 pb-2 text-muted">
            {{ order.item_count }} item{{ order.item_count !== 1 ? 's' : '' }}
          </div>

          <b-collapse :visible="expandedOrderId === order.id">
            <div class="order-details px-3 pb-3">
              <hr class="mt-0" />
              <div
                v-for="product in order.products"
                :key="product.id"
                class="order-line-item d-flex justify-content-between mb-2"
              >
                <div>
                  <span>{{ product.name }}</span>
                  <span class="text-muted ml-1">x{{ product.line_quantity }}</span>
                </div>
                <span>R{{ formatPrice(product.line_total) }}</span>
              </div>
              <hr />
              <div class="d-flex justify-content-between">
                <strong>Total</strong>
                <strong>R{{ formatPrice(order.order_total) }}</strong>
              </div>
            </div>
          </b-collapse>
        </b-card>
      </div>
    </div>

    <cart-bottom-nav
      active-tab="orders"
      :cart-count="cartItemCount"
      @navigate="handleNavigate"
    />
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { memberOrderingComputed, memberOrderingMethods } from "@/state/helpers";
import CartBottomNav from "@/components/member/cartBottomNav.vue";

export default {
  components: { Layout, PageHeader, CartBottomNav },
  page: { title: "My Orders" },
  data() {
    return {
      expandedOrderId: null,
    };
  },
  computed: {
    ...memberOrderingComputed,
    breadcrumbs() {
      return [{ text: "My Orders", active: true }];
    },
  },
  methods: {
    ...memberOrderingMethods,
    formatPrice(val) {
      return parseFloat(val).toFixed(2);
    },
    statusVariant(statusName) {
      if (!statusName) return "secondary";
      const name = statusName.toLowerCase();
      if (name.includes("confirm") || name.includes("complete") || name.includes("paid")) return "success";
      if (name.includes("dispatch") || name.includes("ready")) return "info";
      if (name.includes("pending") || name.includes("process")) return "warning";
      if (name.includes("cancel") || name.includes("fail")) return "danger";
      return "secondary";
    },
    toggleOrder(orderId) {
      this.expandedOrderId = this.expandedOrderId === orderId ? null : orderId;
    },
    handleNavigate(tab) {
      const routes = {
        shop: "member-shop",
        cart: "member-cart",
        orders: "member-orders",
        profile: "member-profile",
      };
      if (routes[tab] && this.$route.name !== routes[tab]) {
        this.$router.push({ name: routes[tab] });
      }
    },
  },
  created() {
    this.fetchMemberOrders();
  },
};
</script>

<style scoped lang="scss">
@import "~@/design/tokens/brand";
@import "~@/design/tokens/typography";
@import "~@/design/tokens/spacing";
@import "~@/design/tokens/surfaces";
@import "~@/design/tokens/feedback";

.order-card {
  background: $surface-card;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: $brand-primary;
  }
}

.order-number {
  font-weight: 600;
  font-size: $type-body;
}

.order-date {
  font-size: $type-small;
}

.order-total {
  font-size: $type-body-lg;
}

.order-line-item {
  font-size: $type-body;
}

// Skeleton loading
.skeleton-card {
  background: $surface-card;
}

.skeleton-content {
  .skeleton-line {
    height: 14px;
    background: $surface-elevated;
    border-radius: 4px;
    animation: skeleton-pulse 1.5s ease-in-out infinite;
  }
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
}
</style>
